import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Injector,
  computed,
  forwardRef,
  inject,
  input,
  output,
} from '@angular/core';
import { NgClass } from '@angular/common';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { BaseControlValueAccessor } from '../base-control-value-accessor';
import { IconComponent } from '../../icons/icons';

// Not used yet. Delete if nothing ends up needing it.
@Component({
  selector: 'lib-form-password',
  templateUrl: './form-password.component.html',
  styleUrl: './form-password.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, MatInputModule, MatFormFieldModule, IconComponent, NgClass],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormPasswordComponent),
      multi: true,
    },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => FormPasswordComponent), multi: true },
  ],
})
export class FormPasswordComponent
  extends BaseControlValueAccessor<string>
  implements AfterContentInit, ControlValueAccessor, Validator
{
  private static nextId = 0;
  private injector = inject(Injector);
  private cdRef = inject(ChangeDetectorRef);

  readonly inputId = `form-password-${FormPasswordComponent.nextId++}`;
  hide = true;
  label = input<string>('Password');
  hideRequiredMarker = input<boolean>(false);
  // Right-aligned link in the label row, e.g. "Forgot your password?".
  labelLink = input<string>('');
  labelLinkClick = output<void>();
  autocomplete = input<string>('off');
  readonly = input<boolean>(false);
  placeholder = input<string>('');
  enforcePattern = input<boolean>(false);

  private readonly passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{6,}$/;
  readonly formatMessage =
    'Password must be at least 6 characters with 1 uppercase, 1 lowercase, 1 number, and 1 special character.';

  autocompleteComputed = computed<string>(() =>
    this.autocomplete() === 'off' ? this.inputId : this.autocomplete(),
  );
  patternComputed = computed<string | RegExp>(() =>
    this.enforcePattern() ? this.passwordPattern : '',
  );

  ngAfterContentInit(): void {
    const ngControl = this.injector.get(NgControl, null);
    this.control = (ngControl?.control as FormControl) ?? new FormControl();
  }

  validate(control: AbstractControl): ValidationErrors | null {
    return control.valid ? null : control.errors;
  }

  get inputType(): 'password' | 'text' {
    return this.hide ? 'password' : 'text';
  }

  toggleVisibility(): void {
    this.hide = !this.hide;
    this.cdRef.markForCheck();
  }

  protected override getLabel(): string {
    return this.label() || '';
  }

  override getErrorMessage(errorKey: string): string {
    switch (errorKey) {
      case 'pattern':
      case 'minlength':
        return this.formatMessage;
      case 'equalTo':
        return 'Passwords do not match';
      default:
        return super.getErrorMessage(errorKey);
    }
  }
}
